import type { QueryKey as TanstackQueryKey } from "@tanstack/react-query";

import type {
	AdminAnalyticsDashboardParams,
	Chat,
	ContentSource,
	DocumentTemplatePartScope,
	GenerateDocumentSourceType,
	IntegrationScopeTypes,
} from "@virtual-brain-lib/types";

/**
 * You define your QUERY_KEYS dictionary here.
 *
 * Each entry can be either a plain array or a function that takes a single parameter.
 */
export const QUERY_KEYS = {
	healthcheck: ["healthcheck"],
	adminAnalyticsDashboard: (params: AdminAnalyticsDashboardParams) => [
		"adminAnalyticsDashboard",
		params,
	],
	userMe: ["userMe"],
	promptCategories: ["promptCategories"],
	chatPromptCategories: ["chatPromptCategories"],
	prompts: ["prompts"],
	teams: ["teams"],
	teamsBrains: (params: { brainId: string }) => ["teamsBrains", params],
	teamsApplications: (params: { applicationId: string }) => ["teamsApplications", params],
	memoryInsights: ["memoryInsights"],
	users: (params: { includeBlocked: boolean }) => ["users", params],
	me: ["me"],
	userTeams: ["userTeams"],
	companyBySlug: (params: { slug: string }) => ["companyBySlug", params],
	companyCredentialPolicy: (params: {
		credentialId: string;
		policy: "whitelist" | "blacklist";
		list: string[];
	}) => ["companyCredentialPolicy", params],
	allChats: (params: { search?: string }) => ["allChats", params],
	chat: (params: { id: string }) => ["chat", params],
	brainStatus: (params: { brainId: string }) => ["brainStatus", params],
	brain: (params: { brainId: string }) => ["brain", params],
	brainIntegrations: (params: { brainId: string }) => ["brainIntegrations", params],
	connectedAccounts: ["connectedAccounts"],
	getDocuments: (params: { query: string; brainsIds: string[] }) => ["getDocuments", params],
	contentsByIds: (params: { contentIds: string[] }) => ["contentsByIds", params],
	files: (params: {
		brainId: string;
		query: string;
		limit: number;
		sort?: string | undefined;
		source: ContentSource[];
		filter?: string | undefined;
	}) => ["files", params],
	integrationFiles: (params: {
		integrationIds: string[];
		scopeType?: IntegrationScopeTypes;
		folder?: string | undefined;
		sort?: string | undefined;
		query?: string | undefined;
	}) => ["integrationFiles", params],
	brains: (params: { accessToken?: string; search?: string }) => ["brains", params],
	applicationData: (params: { applicationId: string }) => ["applicationData", params],
	applicationSession: (params: { sessionId: string }) => ["applicationSession", params],
	applicationSessionMessages: (params: { sessionId: string; blockIds: string[] }) => [
		"applicationSessionMessages",
		params,
	],
	applicationSessionContent: (params: { sessionId: string }) => [
		"applicationSessionContent",
		params,
	],
	applicationBlockMessageContent: (params: { blockMessageId: string }) => [
		"applicationBlockMessageContent",
		params,
	],
	applications: ["applications"],
	mySessions: ["mySessions"],
	applicationTemplates: ["applicationTemplates"],
	templateSteps: (params: { templateId: string }) => ["templateSteps", params],
	integration: (params: { integrationId: string }) => ["integration", params],
	chatbotPreview: (params: { chatbotId?: string }) => ["chatbotPreview", params],
	chatbot: (params: { chatbotId: string; accessToken: string }) => ["chatbotPreview", params],
	artifact: (params: { artifactId: string }) => ["artifact", params],
	artifactDocument: (params: { artifactId: string; documentId: string }) => [
		"artifactDocument",
		params,
	],
	artifactDocumentPreview: (params: { artifactId: string; documentId: string }) => [
		"artifactDocumentPreview",
		params,
	],
	blockChatMessage: (params: { messageId: string }) => ["blockChatMessage", params],
	blockChatMessages: (params: { sessionId: string; blockId: string }) => [
		"blockChatMessages",
		params,
	],
	getBlockChildrens: (params: { blockParentId: string }) => ["getBlockChildrens", params],
	contentData: (params: { contentId: string }) => ["contentData", params],
	companyDocumentTemplates: ["companyDocumentTemplates"],
	personalDocumentTemplates: ["personalDocumentTemplates"],
	documentTemplateDecks: ["documentTemplateDecks"],
	documentTemplateParts: (params: {
		scope?: DocumentTemplatePartScope;
		search?: string;
		templateId?: string;
	}) => ["documentTemplateParts", params],
	recommendedDocumentTemplateParts: (params: {
		sourceType: GenerateDocumentSourceType;
		sourceId: string;
	}) => ["recommendedDocumentTemplateParts", params],
	adminDocumentTemplateParts: (params: {
		templateId: string;
		limit?: number;
		offset?: number;
	}) => ["adminDocumentTemplateParts", params],
	personalDocumentTemplateParts: (params: {
		templateId: string;
		limit?: number;
		offset?: number;
	}) => ["personalDocumentTemplateParts", params],
	generatedDocuments: (params: {
		sourceType?: GenerateDocumentSourceType;
		sourceId?: string;
		limit?: number;
		offset?: number;
	}) => ["generatedDocuments", params],
	brainRelatedApps: (params: { brainId: string }) => ["brainRelatedApps", params],
	brainUsersBrains: (params: { brainId: string }) => ["brainUsersBrains", params],
	apiKeysByChatbotId: (params: { chatbotId: string }) => ["apiKeysByChatbotId", params],
	companyUsers: ["companyUsers"],
	chatMessages: (params: { chatId: Chat["id"] }) => ["chatMessages", params],
	chatbotMessages: (params: { chatbotId: string; chatbotSessionId?: string }) => [
		"chatbot-messages",
		params,
	],
	contentFile: (params: { contentId: string }) => ["contentFile", params],
	generatedDocumentFile: (params: { contentId: string }) => ["generatedDocumentFile", params],
	contentParentFolder: (params: { contentId: string }) => ["contentParentFolder", params],
	contentSummaryAndTitle: (params: { contentId: string }) => ["contentSummaryAndTitle", params],
	vectorData: (params: { contentId: string; vectorId: string }) => ["vectorData", params],
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
} as const satisfies Record<string, ((args: any) => TanstackQueryKey) | TanstackQueryKey>;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function makeQueryKeysOptional<T extends Record<string, any>>(queryKeys: T) {
	return Object.fromEntries(
		Object.entries(queryKeys).map(([key, value]) => {
			if (typeof value === "function") {
				// eslint-disable-next-line @typescript-eslint/no-explicit-any,@typescript-eslint/no-unsafe-return,@typescript-eslint/no-unsafe-call
				return [key, (params?: any) => (params === undefined ? [key] : value(params))];
			}

			return [key, value];
		}),
	) as {
		[K in keyof T]: T[K] extends (args: infer P) => infer R
			? (args?: Partial<P>) => R | readonly [K]
			: T[K];
	};
}

export const QUERY_KEYS_OPTIONAL = makeQueryKeysOptional(QUERY_KEYS);
